import React, { useState, useRef, useEffect } from "react";
import { useLoadScript } from "@react-google-maps/api";
import { GOOGLE_MAPS_CONFIG, isGoogleMapsConfigured } from "../../config/googleMaps";
import InputText from "../InputFields/InputText";
import "../InputFields/InputFields.css";

const InlineSpinner = () => (
  <span
    className="cs-gpa-spinner"
    aria-hidden
    style={{
      width: 20,
      height: 20,
      border: "2px solid #e5e7eb",
      borderTopColor: "#64748b",
      borderRadius: "50%",
      display: "inline-block",
      animation: "cs-spin 0.7s linear infinite",
    }}
  />
);

const GooglePlacesAutocomplete = ({
  label,
  value,
  onChange,
  placeholder,
  required = false,
  error = false,
  helperText,
  fullWidth = true,
  disabled = false,
  disableClearable = false,
  apiKey,
  onPlaceSelected,
  onFocus,
  /** Extra controls after spinner/clear (e.g. “Custom” address) — sits inside the input row */
  extraEndAdornment,
  ...props
}) => {
  const [inputValue, setInputValue] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [autocompleteService, setAutocompleteService] = useState(null);
  const [placesService, setPlacesService] = useState(null);
  const wrapRef = useRef(null);

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: apiKey || GOOGLE_MAPS_CONFIG.apiKey,
    libraries: GOOGLE_MAPS_CONFIG.libraries,
  });

  useEffect(() => {
    if (isLoaded && window.google) {
      setAutocompleteService(
        new window.google.maps.places.AutocompleteService()
      );
      setPlacesService(
        new window.google.maps.places.PlacesService(
          document.createElement("div")
        )
      );
    }
  }, [isLoaded]);

  useEffect(() => {
    setInputValue(value || "");
  }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const handleInputChange = (event, newInputValue) => {
    const v =
      typeof newInputValue === "string"
        ? newInputValue
        : event?.target?.value ?? "";
    setInputValue(v);

    if (v.length > 2 && autocompleteService) {
      setLoading(true);

      autocompleteService.getPlacePredictions(
        {
          input: v,
          ...GOOGLE_MAPS_CONFIG.autocompleteOptions,
        },
        (predictions, status) => {
          setLoading(false);
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            predictions
          ) {
            setSuggestions(
              predictions.map((prediction) => ({
                description: prediction.description,
                placeId: prediction.place_id,
                structuredFormatting: prediction.structured_formatting,
              }))
            );
            setMenuOpen(true);
          } else {
            setSuggestions([]);
            setMenuOpen(false);
          }
        }
      );
    } else {
      setSuggestions([]);
      setMenuOpen(false);
    }
  };

  const handlePlaceSelect = (selectedPlace) => {
    if (selectedPlace && placesService) {
      placesService.getDetails(
        {
          placeId: selectedPlace?.placeId,
          fields: GOOGLE_MAPS_CONFIG.autocompleteOptions.fields,
        },
        (place, status) => {
          if (
            status === window.google.maps.places.PlacesServiceStatus.OK &&
            place
          ) {
            const addressComponents = place.address_components;
            const formattedAddress = place.formatted_address;

            let streetNumber = "";
            let route = "";
            let city = "";
            let state = "";
            let zipCode = "";
            let country = "";

            addressComponents.forEach((component) => {
              const types = component.types;

              if (types.includes("street_number")) {
                streetNumber = component.long_name;
              } else if (types.includes("route")) {
                route = component.long_name;
              } else if (types.includes("locality")) {
                city = component.long_name;
              } else if (types.includes("administrative_area_level_2")) {
                if (!city) city = component.long_name;
              } else if (types.includes("administrative_area_level_1")) {
                state = component.long_name;
              } else if (types.includes("postal_code")) {
                zipCode = component.long_name;
              } else if (types.includes("country")) {
                country = component.long_name;
              }
            });

            if (!city || !state || !country) {
              const addressParts = formattedAddress
                .split(",")
                .map((part) => part.trim());

              if (!country && addressParts.length > 0) {
                const lastPart = addressParts[addressParts.length - 1];
                if (lastPart && lastPart.length > 2) {
                  country = lastPart;
                }
              }

              if (!state && addressParts.length > 1) {
                const secondLastPart = addressParts[addressParts.length - 2];
                if (secondLastPart && secondLastPart.length > 1) {
                  state = secondLastPart;
                }
              }

              if (!city && addressParts.length > 2) {
                const thirdLastPart = addressParts[addressParts.length - 3];
                if (thirdLastPart && thirdLastPart.length > 1) {
                  city = thirdLastPart;
                }
              }

              if (!zipCode) {
                for (const part of addressParts) {
                  if (/^\d{5}(-\d{4})?$/.test(part)) {
                    zipCode = part;
                    break;
                  }
                }
              }
            }

            const streetAddress = `${streetNumber} ${route}`.trim();

            const payload = {
              street: streetAddress,
              city: city,
              state: state,
              zip: zipCode,
              country: country,
              formatted_address: formattedAddress,
              place_id: selectedPlace?.placeId,
            };
            if (onChange) {
              onChange(payload);
            }
            if (onPlaceSelected) {
              onPlaceSelected(payload);
            }

            setInputValue(formattedAddress);
          } else {
            const fallbackData = {
              street:
                selectedPlace?.description ||
                selectedPlace?.structuredFormatting?.main_text ||
                "",
              city: "",
              state: "",
              zip: "",
              country: "",
              formatted_address: selectedPlace?.description || "",
              place_id: selectedPlace?.placeId,
            };

            if (onChange) {
              onChange(fallbackData);
            }
            if (onPlaceSelected) {
              onPlaceSelected(fallbackData);
            }
            setInputValue(selectedPlace?.description || "");
          }
          setMenuOpen(false);
        }
      );
    }
  };

  const fallbackHelper =
    helperText ||
    (!isGoogleMapsConfigured()
      ? "Google Maps API key not configured. Please enter address manually."
      : "Google Places API failed to load. Please enter address manually.");

  const shellStyle = {
    width: fullWidth ? "100%" : undefined,
    position: "relative",
  };

  if (loadError || !isGoogleMapsConfigured()) {
    return (
      <div style={shellStyle}>
        <InputText
          {...props}
          fullWidth={fullWidth}
          label={label}
          required={required}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (onChange) {
              onChange({ street: e.target.value });
            }
          }}
          onFocus={onFocus}
          placeholder={placeholder}
          disable={disabled}
          error={error}
          helperText={fallbackHelper}
          autoComplete="off"
          endAdornment={extraEndAdornment}
        />
      </div>
    );
  }

  if (!isLoaded) {
    return (
      <div style={shellStyle}>
        <InputText
          {...props}
          fullWidth={fullWidth}
          label={label}
          required={required}
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            if (onChange) {
              onChange({ street: e.target.value });
            }
          }}
          onFocus={onFocus}
          placeholder={placeholder}
          disable={disabled}
          error={error}
          helperText={helperText}
          autoComplete="off"
          endAdornment={
            <>
              <InlineSpinner />
              {extraEndAdornment}
            </>
          }
        />
        <style>{`@keyframes cs-spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={shellStyle} className="cs-gpa-wrap">
      <style>{`@keyframes cs-spin { to { transform: rotate(360deg); } }`}</style>
      <InputText
        {...props}
        fullWidth={fullWidth}
        label={label}
        required={required}
        value={inputValue}
        onChange={(e) => {
          setInputValue(e.target.value);
          handleInputChange(e, e.target.value);
          if (onChange) {
            onChange({ street: e.target.value });
          }
        }}
        onFocus={onFocus}
        placeholder={placeholder}
        disable={disabled}
        error={error}
        helperText={helperText}
        autoComplete="off"
        endAdornment={
          <>
            {loading ? <InlineSpinner /> : null}
            {!disableClearable && inputValue ? (
              <button
                type="button"
                className="cs-gpa-clear"
                onClick={() => {
                  setInputValue("");
                  setSuggestions([]);
                  setMenuOpen(false);
                  if (onChange) onChange({ street: "" });
                }}
                aria-label="Clear"
              >
                ×
              </button>
            ) : null}
            {extraEndAdornment}
          </>
        }
      />
      {menuOpen && suggestions.length > 0 && (
        <ul
          className="cs-gpa-menu"
          role="listbox"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "100%",
            marginTop: 4,
            zIndex: 50,
            listStyle: "none",
            padding: 4,
            margin: 0,
            background: "#fff",
            border: "1px solid #1a3f5c",
            borderRadius: 8,
            maxHeight: 280,
            overflowY: "auto",
            boxShadow: "var(--shadow-lg)",
          }}
        >
          {suggestions.map((option) => (
            <li
              key={option.placeId}
              role="option"
              style={{
                padding: "10px 12px",
                cursor: "pointer",
                borderRadius: 6,
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handlePlaceSelect(option)}
            >
              <div style={{ fontSize: 14, color: "#374151", fontWeight: 500 }}>
                {option.structuredFormatting?.main_text || option.description}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                {option.structuredFormatting?.secondary_text}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default GooglePlacesAutocomplete;
